/**
 * TaskManager.ts
 *
 * 작업 CRUD(Create, Read, Update, Delete) 작업을 담당하는 모듈
 *
 * 주요 기능:
 * - Task 생성, 수정, 삭제
 * - 반복 작업 처리 (이번만, 이후 모두, 전체 삭제)
 * - Task 검색 및 이동
 * - ICS 파일 생성 및 파싱
 *
 * 이 모듈은 사용자의 Task (VTodo) 관련 모든 작업을 처리하며,
 * CalDAV 서버와의 Task(VTodo) 데이터 교환을 담당합니다.
 */

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import moment from 'moment'
import 'moment-timezone'

dayjs.extend(utc)

import { getClient } from 'pages/calendar/modules/CalendarClient'
import { RRule, datetime } from 'rrule'
import { TodoFolder } from './TodoFolder'
import { getFolders /* , getUserInfo */ } from './TodoFolderSync'
import { TodoItem, secondsDuration, syncState } from './TodoItem'
import * as todoService from './TodoService'
import { generateVTodoICS } from './VTodoCreator'

import settings from 'popups/Settings'

/**
 * Helper function to normalize dates by setting seconds and milliseconds to 0
 * @param date - Date to normalize
 * @returns Normalized date with seconds and milliseconds set to 0
 */
const normalizeWithoutSeconds = (date: Date | null): Date | null => {
  if (!date) return null

  // 유효성 검사
  if (isNaN(date.getTime())) {
    console.warn('⚠️ normalizeWithoutSeconds received invalid date:', date)
    return null
  }

  const normalized = new Date(date)
  normalized.setSeconds(0, 0) // Set seconds and milliseconds to 0
  return normalized
}

export const TaskDeleteType = {
  ALL: 0,
  THIS_ONLY: 1,
  AFTER_ALL: 2,
} as const

export type TaskDeleteType = (typeof TaskDeleteType)[keyof typeof TaskDeleteType]

/**
 * Task 생성
 * @param todo TodoItem 객체
 * @param todoFolder 대상 폴더
 * @param completeCallback 완료 콜백
 */
export const createTask = (
  todo: TodoItem,
  todoFolder: TodoFolder,
  completeCallback = (_success: boolean, _url?: string) => {},
) => {
  const client = getClient()

  const ics = generateVTodoICS(todo, null)
  client
    .createCalendarObject({
      calendar: todoFolder as any,
      iCalString: ics,
      filename: `${todo.uid}.ics`,
    })
    .then(async (response) => {
      if (Math.trunc(response.status / 100) === 2) {
        console.log('create task success %o', response)

        // 2. 서버 응답으로 실제 데이터 업데이트 (url 은 포함되어 있으나 etag 은 포함되어 있지 않음)
        if (response && response.url) {
          try {
            const serverTodo = {
              ...todo,
              url: response.url,
              //syncState: syncState.SERVER, // 서버 생성 성공 시 SERVER 상태로 설정
            }

            await todoService.saveTodo(serverTodo)
            console.log('Todo updated with server response data and SERVER state')
          } catch (dbError) {
            console.error('Failed to update todo with server data:', dbError)
          }
        }

        // // 3. 해당 폴더만 동기화 (전체 reloadTodos 대신)
        // try {
        //   const { todoItemEmitter } = await import('./TodoSync')
        //   todoItemEmitter.emit('item')
        //   console.log('[createTask] UI update triggered for new todo')
        // } catch (error) {
        //   console.error('[createTask] Failed to trigger UI update:', error)
        // }

        // Callback 먼저 실행하여 UI 빠르게 업데이트
        completeCallback(true, response.url)

        // Folder-specific reload로 성능 개선
        const { reloadTodosForFolder } = await import('./TodoSync')
        reloadTodosForFolder(todoFolder.url, (reloadSuccess, updatedTodos) => {
          if (reloadSuccess && updatedTodos) {
            const freshTodo = updatedTodos.find((t) => t.url === response.url)
            console.log('✅ Created todo with fresh etag:', freshTodo?.etag)
          }
          // callback은 이미 실행되었으므로 여기서는 로그만
        })
      } else {
        console.log('create task failed : %o', response)

        // 3. 실패 시 rollback - 메모리 캐시에서 제거
        try {
          // todo.url이 null인 경우 uid를 사용
          await todoService.deleteTodo(todo.url || todo.uid, todo.folderUrl)
          console.log('Rollback: removed failed todo from cache')
        } catch (rollbackError) {
          console.error('Failed to rollback todo from cache:', rollbackError)
        }

        completeCallback(false)
      }
      const localSettings = settings.getValue('localSettings')
      settings.setValue('localSettings', {
        ...localSettings,
        todo: {
          ...localSettings.todo,
          lastAddedFolder: todoFolder.url,
        },
      })
    })
    .catch(async (error) => {
      console.log('create task failed : %o', error)

      // 4. 에러 시 rollback - 메모리 캐시에서 제거
      try {
        // todo.url이 null인 경우 uid를 사용
        await todoService.deleteTodo(todo.url || todo.uid, todo.folderUrl)
        console.log('Rollback: removed failed todo from cache')
      } catch (rollbackError) {
        console.error('Failed to rollback todo from cache:', rollbackError)
      }

      completeCallback(false)
    })
}

/**
 * Task 수정
 * @param serverId 서버 ID
 * @param etag ETag
 * @param todo TodoItem 객체
 * @param moveToFolder 이동할 폴더 (선택사항)
 * @param completeCallback 완료 콜백
 */
export const updateTask = (
  todoUrl: string,
  etag: string,
  todo: TodoItem,
  moveToFolder: TodoFolder | null = null,
  completeCallback = (_success: boolean, _updatedTodo?: TodoItem) => {},
) => {
  const client = getClient()

  // 1. 백업용 원본 데이터 저장 (rollback을 위해)
  let originalTodo: TodoItem | null = null

  // 2. 즉시 메모리 캐시 업데이트 (빠른 UI 업데이트)
  const optimisticUpdate = async () => {
    try {
      const existingTodos = await todoService.getTodoItems(todo.folderUrl)
      originalTodo = existingTodos.find((t) => t.url === todoUrl) || null

      // 사용자가 수정한 todo는 LOCAL_DIRTY 상태로 설정
      const localTodo = { ...todo, syncState: syncState.LOCAL_DIRTY }
      await todoService.saveTodo(localTodo)
      console.log('Optimistic todo updated in cache with LOCAL_DIRTY state')
    } catch (error) {
      console.error('Failed to optimistically update todo in cache:', error)
    }
  }

  optimisticUpdate()

  const ics = generateVTodoICS(todo)
  client
    .updateCalendarObject({
      calendarObject: {
        url: todoUrl,
        etag: etag,
        data: ics,
      },
    })
    .then(async (response) => {
      console.log('update task response %o / do move %d', response, !!moveToFolder)

      // 412 Precondition Failed (ETag mismatch) 처리
      if (response.status === 412) {
        console.warn('ETag mismatch (412), need to retry with fresh ETag')

        // 실패 시 rollback - 원본 데이터로 복구
        if (originalTodo) {
          try {
            await todoService.saveTodo(originalTodo)
            console.log('Rollback: restored original todo due to ETag mismatch')
          } catch (rollbackError) {
            console.error('Failed to rollback todo:', rollbackError)
          }
        }

        completeCallback(false)
        return
      }

      // 성공 응답이 아닌 경우 처리
      if (!response.ok) {
        console.error('Server update failed with status:', response.status)

        // 실패 시 rollback - 원본 데이터로 복구
        if (originalTodo) {
          try {
            await todoService.saveTodo(originalTodo)
            console.log('Rollback: restored original todo due to server error')
          } catch (rollbackError) {
            console.error('Failed to rollback todo:', rollbackError)
          }
        }

        completeCallback(false)
        return
      }

      console.log('update task success')

      // 3. 서버 응답으로 실제 데이터 업데이트 (url, etag 포함)
      let updatedTodo = todo // update 시에는 response.etag 에 값이 안내려 온다.
      const responseWithEtag = response as any
      if (response && responseWithEtag.url && responseWithEtag.etag) {
        try {
          const serverTodo = {
            ...todo,
            url: responseWithEtag.url,
            etag: responseWithEtag.etag,
            syncState: syncState.SERVER, // 서버 업데이트 성공 시 SERVER 상태로 설정
          }

          await todoService.saveTodo(serverTodo)
          updatedTodo = serverTodo
          console.log('Todo updated with server response data')
        } catch (dbError) {
          console.error('Failed to update todo with server data:', dbError)
        }
      }

      if (!moveToFolder) {
        // update 시에는 해당 folder 만 동기화 처리 한다.
        const { reloadTodosForFolder } = await import('./TodoSync')
        reloadTodosForFolder(todo.folderUrl, (reloadSuccess, updatedTodos) => {
          if (reloadSuccess && updatedTodos) {
            // 동기화 완료 후 최신 데이터에서 해당 todo 찾기
            const freshTodo = updatedTodos.find((t) => t.url === todoUrl)
            if (freshTodo) {
              console.log('updated etag:', freshTodo.etag)
              completeCallback(true, freshTodo)
            } else {
              console.warn('⚠️ Todo not found after sync, using server response')
              completeCallback(true, updatedTodo)
            }
          } else {
            console.warn('⚠️ Folder reload failed, using server response')
            completeCallback(true, updatedTodo)
          }
        })
      } else {
        moveTask(moveToFolder, todoUrl, async (success) => {
          if (success) {
            // Move의 경우도 // update 시에는 해당 folder 만 동기화 처리 한다.
            const { reloadTodosForFolder } = await import('./TodoSync')
            reloadTodosForFolder(moveToFolder.url, (reloadSuccess, updatedTodos) => {
              const freshTodo = updatedTodos?.find((t) => t.url === todoUrl)
              completeCallback(success, freshTodo || updatedTodo)
            })
          } else {
            completeCallback(false)
          }
        })
      }
    })
    .catch(async (error) => {
      console.log('update task failed : %o', error)

      // 4. 실패 시 rollback - 원본 데이터로 복구
      if (originalTodo) {
        try {
          await todoService.saveTodo(originalTodo)
          console.log('Rollback: restored original todo in cache')
        } catch (rollbackError) {
          console.error('Failed to rollback todo in cache:', rollbackError)
        }
      }

      completeCallback(false)
    })
}

/**
 * Task 삭제
 * @param taskInstance Task 인스턴스
 * @param completeCallback 완료 콜백
 */
export const deleteTask = (todo: TodoItem, completeCallback = (_success: boolean) => {}) => {
  const client = getClient()

  // 1. 백업용 원본 데이터 저장 (rollback을 위해)
  const originalTodo = { ...todo }

  // 2. 즉시 메모리 캐시에서 삭제 (빠른 UI 업데이트)
  const optimisticDelete = async () => {
    try {
      await todoService.deleteTodo(todo.url, todo.folderUrl)
      console.log('Optimistic todo deleted from cache')
    } catch (error) {
      console.error('Failed to optimistically delete todo from cache:', error)
    }
  }

  optimisticDelete()

  client
    .deleteCalendarObject({
      calendarObject: {
        url: todo.url,
        etag: todo.etag,
      },
    })
    .then(async () => {
      console.log('delete task success')

      // 3. 서버 삭제 성공 - 이미 캐시에서 삭제되었으므로 DB만 정리
      try {
        await todoService.deleteTodo(todo.url, todo.folderUrl)
        console.log('Todo deleted from DB (server confirmed)')
      } catch (dbError) {
        console.error('Failed to delete todo from DB:', dbError)
      }

      // Callback 먼저 실행하여 UI 업데이트
      completeCallback(true)

      // Folder-specific reload로 성능 개선
      const { reloadTodosForFolder } = await import('./TodoSync')
      reloadTodosForFolder(todo.folderUrl, (reloadSuccess) => {
        console.log(`✅ Delete confirmed by sync: ${reloadSuccess}`)
      })
    })
    .catch(async (error) => {
      console.log('delete task failed : %o', error)

      // 4. 실패 시 rollback - 원본 데이터 복구
      try {
        await todoService.saveTodo(originalTodo)
        console.log('Rollback: restored deleted todo in cache')
      } catch (rollbackError) {
        console.error('Failed to rollback todo in cache:', rollbackError)
      }

      completeCallback(false)
    })
}
/**
 * Recurrence task 삭제 처리 API
 * @param todo - 삭제할 todo 객체
 * @param allTasks - true이면 기존 deleteTask 동작, false이면 recurrence 특별 처리
 * @param completeCallback - 콜백 함수
 */
export const deleteRecurringTask = (
  todo: TodoItem,
  allTasks: boolean = false,
  completeCallback = (_success: boolean) => {},
) => {
  // allTasks가 true이면 기존 deleteTask 동작 수행
  if (allTasks) {
    deleteTask(todo, completeCallback)
    return
  }

  // recurrence rule이 없으면 일반 삭제
  if (!todo.recurrenceRule) {
    deleteTask(todo, completeCallback)
    return
  }

  try {
    // Step 1: 삭제될 인스턴스를 새 UID로 생성
    const deletedDate = new Date()
    const deletedInstance: TodoItem = {
      ...todo,
      uid: generateNewUID(), // 새 UID 생성
      url: null, // 새 인스턴스이므로 null
      etag: null, // 새 인스턴스이므로 null
      status: 'CANCELLED', // 삭제된 상태로 표시
      percentComplete: 0,
      completedDate: null,
      createdDate: deletedDate,
      dtstamp: deletedDate,
      startDate: deletedDate,
      recurrenceRule: null, // 삭제 인스턴스에는 반복 규칙 없음
    }

    // 폴더 찾기
    const folders = getFolders()
    const targetFolder = folders.find((f) => f.url === todo.folderUrl) || folders[0]

    if (!targetFolder) {
      console.error('Target folder not found for recurring todo deletion')
      completeCallback(false)
      return
    }

    // Step 2: 삭제된 인스턴스 생성
    createTask(deletedInstance, targetFolder, (createSuccess, createdUrl) => {
      if (!createSuccess) {
        console.error('Failed to create deleted recurring instance')
        completeCallback(false)
        return
      }

      console.log(`Successfully created deleted instance with UID: ${deletedInstance.uid}`)

      // 생성된 URL을 deletedInstance에 설정
      if (createdUrl) {
        deletedInstance.folderUrl = targetFolder.url
        deletedInstance.url = createdUrl
      }

      // Step 3: 기존 todo를 다음 날짜로 업데이트 (동일한 UID 유지)
      updateOriginalRecurringTodo(todo, (updateSuccess) => {
        if (!updateSuccess) {
          console.error('Failed to update original recurring todo to next occurrence')
          completeCallback(false)
          return
        }

        console.log(`Successfully updated original recurring todo to next occurrence`)

        // Step 4: 생성된 삭제 인스턴스를 실제로 삭제
        deleteTask(deletedInstance, (deleteSuccess) => {
          if (deleteSuccess) {
            console.log(
              `Successfully deleted the created instance with UID: ${deletedInstance.uid}`,
            )

            // // Step 5: 성공 시 todo list 업데이트 (deleteTask와 동일한 처리)
            // // 서버에서 성공적으로 삭제되었으면 로컬 연동
            // todoService.deleteTodo(todo.url || todo.uid, todo.folderUrl)
            // console.log('Successfully deleted original recurring todo from local service')

            // 모든 sync 폴더에 대해 동기화 수행 (다른 곳에서 삭제된 todo도 함께 업데이트)
            // reloadTodos()
            console.log('Todo list reloaded after recurring task deletion')
          } else {
            console.error('Failed to delete the created instance')
          }
          completeCallback(deleteSuccess)
        })
      })
    })
  } catch (error) {
    console.error('Error deleting recurring task:', error)
    completeCallback(false)
  }
}

/**
 * UID로 Task 삭제
 * @param uid Task UID
 * @param recurrenceId 반복 ID (선택사항)
 * @param completeCallback 완료 콜백
 */
export const deleteTaskByUid = (
  uid: string,
  recurrenceId?: string,
  completeCallback = (_success: boolean) => {},
) => {
  findTaskByUid(uid, recurrenceId, (taskInstance) => {
    if (taskInstance) {
      deleteTask(taskInstance, completeCallback)
    } else {
      completeCallback(false)
    }
  })
}

/**
 * Task 상태 업데이트 (완료/미완료)
 * @param todo Task 인스턴스
 * @param completed 완료 여부
 * @param completeCallback 완료 콜백
 */
export const updateTaskStatus = (
  todo: TodoItem,
  completed: boolean,
  completeCallback = (_success: boolean, _updatedTodo?: TodoItem) => {},
) => {
  // 반복 todo가 완료될 때 특별 처리
  if (completed && todo.recurrenceRule) {
    // 1. 완료된 인스턴스를 새 UID로 생성
    // 2. 기존 todo를 다음 날짜로 업데이트 (동일한 UID 유지)
    createCompletedRecurringInstance(todo, completeCallback)
  } else {
    // 일반 todo 또는 반복 todo의 미완료 처리 (완료된 반복 todo의 uncomplete 포함)
    const updatedTodo: TodoItem = {
      ...todo,
      status: completed ? 'COMPLETED' : 'NEEDS-ACTION',
      percentComplete: completed ? 100 : 0,
      completedDate: completed ? new Date() : null,
    }

    updateTask(todo.url, todo.etag, updatedTodo, null, (success, serverTodo) => {
      if (!success) {
        // ETag 실패 시 최신 데이터로 재시도
        console.log('Update failed, attempting to get fresh ETag and retry')

        // 최신 todo 데이터를 가져와서 재시도
        todoService
          .getTodoItems(todo.folderUrl)
          .then((todos) => {
            const freshTodo = todos.find((t) => t.url === todo.url)
            if (freshTodo && freshTodo.etag !== todo.etag) {
              console.log('Found fresh ETag, retrying update')
              const freshUpdatedTodo: TodoItem = {
                ...freshTodo,
                status: completed ? 'COMPLETED' : 'NEEDS-ACTION',
                percentComplete: completed ? 100 : 0,
                completedDate: completed ? new Date() : null,
              }

              updateTask(
                freshTodo.url,
                freshTodo.etag,
                freshUpdatedTodo,
                null,
                (retrySuccess, retryServerTodo) => {
                  completeCallback(retrySuccess, retryServerTodo)
                },
              )
            } else {
              console.error('Could not find fresh todo or ETag is same')
              completeCallback(false)
            }
          })
          .catch((error) => {
            console.error('Failed to get fresh todo data:', error)
            completeCallback(false)
          })
      } else {
        completeCallback(success, serverTodo)
      }
    })
  }
}

/**
 * Task 우선순위 업데이트
 * @param todo Task 인스턴스
 * @param isImportant 중요 여부 (true: HIGH, false: MEDIUM)
 * @param completeCallback 완료 콜백
 */
export const updateTaskPriority = (
  todo: TodoItem,
  isImportant: boolean,
  completeCallback = (_success: boolean) => {},
) => {
  const updatedTodo: TodoItem = {
    ...todo,
    priority: isImportant ? 'HIGH' : 'MEDIUM',
  }

  updateTask(todo.url, todo.etag, updatedTodo, null, completeCallback)
}

/**
 * CalDAV 표준에 따른 반복 todo 완료 처리
 * - 완료된 인스턴스를 새 UID로 생성
 * - 기존 반복 todo는 다음 날짜로 업데이트 (동일한 UID 유지)
 * @param originalTodo 원본 반복 todo
 * @param completeCallback 완료 콜백
 */
export const createCompletedRecurringInstance = (
  originalTodo: TodoItem,
  completeCallback = (_success: boolean, _updatedTodo?: TodoItem) => {},
) => {
  if (!originalTodo.recurrenceRule) {
    console.log('No recurrence rule found')
    completeCallback(false)
    return
  }

  try {
    // Step 1: 완료된 인스턴스 생성 (새 UID)
    // const recurrenceIdDate = originalTodo.dueDate || new Date()
    const completedDate = new Date()
    const completedInstance: TodoItem = {
      ...originalTodo,
      uid: generateNewUID(), // 새 UID 생성
      url: null, // 새 인스턴스이므로 null
      etag: null, // 새 인스턴스이므로 null
      status: 'COMPLETED',
      percentComplete: 100,
      completedDate: completedDate,
      createdDate: completedDate,
      dtstamp: completedDate,
      startDate: completedDate,
      // recurrenceId: recurrenceIdDate, // 완료된 특정 인스턴스 식별 - RECURRENCE-ID 처리 주석
      recurrenceRule: null, // 완료 인스턴스에는 반복 규칙 없음
    }

    // 폴더 찾기
    const folders = getFolders()
    const targetFolder = folders.find((f) => f.url === originalTodo.folderUrl) || folders[0]

    if (!targetFolder) {
      console.error('Target folder not found for recurring todo')
      completeCallback(false)
      return
    }

    // Step 2: 완료된 인스턴스 생성
    createTask(completedInstance, targetFolder, (createSuccess, _createdUrl) => {
      if (!createSuccess) {
        console.error('Failed to create completed recurring instance')
        completeCallback(false)
        return
      }

      console.log(`Successfully created completed instance with UID: ${completedInstance.uid}`)

      // Step 3: 기존 todo를 다음 날짜로 업데이트 (동일한 UID 유지)
      updateOriginalRecurringTodo(originalTodo, (updateSuccess) => {
        if (updateSuccess) {
          console.log(`Successfully updated original recurring todo to next occurrence`)
          // 성공 시 콜백 호출 - completed todo를 전달하지 않음 (UI가 next instance를 표시해야 함)
          completeCallback(true)
        } else {
          console.error('Failed to update original recurring todo to next occurrence')
          completeCallback(false)
        }
      })
    })
  } catch (error) {
    console.error('Error creating completed recurring instance:', error)
    completeCallback(false)
  }
}

/**
 * 새로운 UID 생성
 * @returns 새로운 UID
 */
const generateNewUID = (): string => {
  return crypto.randomUUID()
}

/**
 * 다음 반복 날짜 계산
 * @param rrule 반복 규칙
 * @param currentInstanceDate 현재 인스턴스 날짜
 * @returns 다음 반복 날짜 또는 null (반복 종료)
 */
const calculateNextRecurrenceDate = (
  rrule: RRule,
  currentInstanceDate: string | Date,
): Date | null => {
  try {
    console.log('RRule DTSTART:', rrule.origOptions.dtstart)
    console.log('Current instance date:', currentInstanceDate)

    // 현재 인스턴스 날짜를 Date 객체로 변환
    const currentDate = new Date(currentInstanceDate)

    // RRule 문서 권장: 단순하게 처리
    // 다음 날 찾기를 위해 현재 날의 마지막 순간으로 설정
    const afterCurrentInstance = new Date(currentDate)
    afterCurrentInstance.setHours(23, 59, 59, 999)

    console.log('After current instance (end of day):', afterCurrentInstance)

    // RRule.after()는 이미 로컬 타임존으로 해석된 결과를 반환
    // 테스트에서 검증된 패턴: exclusive search (false) 사용
    let nextDate = rrule.after(afterCurrentInstance, false)

    if (!nextDate) {
      console.log('No more recurring instances found')
      return null
    }

    console.log('Next recurring date (from RRule):', nextDate)

    // RRule의 dtstart 정보 로깅 (디버깅용)
    const dtstart = rrule.origOptions.dtstart
    if (dtstart) {
      console.log('RRule dtstart info:', {
        utc: dtstart.toISOString(),
        hours: dtstart.getUTCHours(),
        minutes: dtstart.getUTCMinutes(),
        seconds: dtstart.getUTCSeconds(),
      })
    }

    console.log('Final result date (no timezone conversion):', nextDate)
    return nextDate
  } catch (error) {
    console.error('Error calculating next recurrence date:', error)
    return null
  }
}

/**
 * 다음 인스턴스를 위한 날짜들 조정
 * @param originalTodo 원본 Todo
 * @param nextDate 다음 반복 날짜
 * @returns 조정된 시작일과 마감일
 */
const adjustDatesForNextInstance = (
  originalTodo: TodoItem,
  nextDate: Date,
  datetype: string = 'date',
): { newStartDate: Date | null; newDueDate: Date | null } => {
  // datetype이 'date'인 경우 시간을 무시하고 날짜만 사용
  if (datetype === 'date') {
    const newStartDate = originalTodo.startDate
      ? new Date(moment(nextDate).format('YYYY-MM-DD'))
      : null

    const newDueDate = originalTodo.dueDate ? new Date(moment(nextDate).format('YYYY-MM-DD')) : null

    return { newStartDate, newDueDate }
  }

  // datetype이 'date-time'인 경우 시간을 포함하여 처리
  const newStartDate = originalTodo.startDate
    ? new Date(
        adjustDateForNextInstance(originalTodo.startDate, nextDate, originalTodo.dueDate) ||
          nextDate,
      )
    : null

  const newDueDate = originalTodo.dueDate
    ? new Date(
        adjustDateForNextInstance(originalTodo.dueDate, nextDate, originalTodo.dueDate) || nextDate,
      )
    : null

  return { newStartDate, newDueDate }
}

/**
 * Recurrence에서 startDate 기반으로 알림 업데이트
 * @param originalAlarms 원본 알림 목록
 * @param newStartDate 새로운 startDate (reminder 시간)
 * @param newDueDate 새로운 dueDate
 * @param datetype 데이터 타입
 * @returns 업데이트된 알림 목록
 */
const updateAlarmsForRecurrenceWithStartDate = (
  originalAlarms: any[],
  newStartDate: Date | null,
): any[] => {
  if (!originalAlarms || originalAlarms.length === 0 || !newStartDate) {
    return originalAlarms || []
  }

  return originalAlarms.map((alarm) => {
    // 상대 시간 알림의 경우 그대로 유지 (dueDate 기준으로 계산됨)
    if (typeof alarm.trigger === 'string' && alarm.trigger.startsWith('-')) {
      return alarm
    }

    // 절대 시간 알림 처리
    if (alarm.trigger && typeof alarm.trigger === 'object' && alarm.trigger.val) {
      try {
        // newStartDate는 로컬 시간이므로 UTC 변환이 필요
        const newStartDateMoment = moment(newStartDate)
        const newReminderTimeUTC = newStartDateMoment.utc().format('YYYYMMDD[T]HHmmss[Z]')

        console.log('🔄 Updated absolute alarm (local to UTC conversion):', {
          originalTriggerVal: alarm.trigger.val,
          newStartDate: newStartDate.toISOString(),
          newStartDateLocal: newStartDateMoment.format('YYYY-MM-DD HH:mm:ss Z'),
          newReminderTimeUTC,
        })

        return {
          action: 'DISPLAY',
          trigger: {
            params: {},
            val: newReminderTimeUTC,
          },
        }
      } catch (error) {
        console.error('Error updating alarm for recurrence:', error)
        return alarm
      }
    }

    return alarm
  })
}

/**
 * Create RRule with proper timezone support based on rrule documentation
 * @param rruleString RRule string (e.g., "FREQ=DAILY;INTERVAL=1")
 * @param dtstart Start date in UTC
 * @param tzid Timezone ID (e.g., "Asia/Seoul")
 * @returns RRule instance with proper timezone handling
 */
export const createRRuleWithTimezone = (
  rruleString: string,
  dtstart: Date | null,
  tzid?: string,
): RRule | null => {
  try {
    if (!rruleString) return null

    // Parse the RRule string to get options
    const tempRule = RRule.fromString(`RRULE:${rruleString}`)
    let rruleOptions = { ...tempRule.origOptions }

    // RRule 문서 권장: datetime() 헬퍼 사용
    if (dtstart) {
      const startDate = new Date(dtstart)
      rruleOptions.dtstart = datetime(
        startDate.getFullYear(),
        startDate.getMonth() + 1, // datetime()은 1-based month
        startDate.getDate(),
        startDate.getHours(),
        startDate.getMinutes(),
        startDate.getSeconds(),
      )
    }

    // Set timezone if provided
    if (tzid) {
      rruleOptions.tzid = tzid
    }

    return new RRule(rruleOptions)
  } catch (error) {
    console.error('Error creating RRule with timezone:', error)
    return null
  }
}

/**
 * 다음 인스턴스 Todo 객체 생성
 * @param originalTodo 원본 Todo
 * @param newStartDate 새 시작일
 * @param newDueDate 새 마감일
 * @param updatedAlarms 업데이트된 알림 목록
 * @returns 새 Todo 객체
 */
const createNextInstanceTodo = (
  originalTodo: TodoItem,
  newStartDate: Date | null,
  newDueDate: Date | null,
  updatedAlarms: any[],
  datetype: string = 'date',
): TodoItem => {
  return {
    ...originalTodo,
    startDate: normalizeWithoutSeconds(newStartDate), // Apply seconds normalization
    dueDate: normalizeWithoutSeconds(newDueDate), // Apply seconds normalization
    datetype: datetype,
    status: 'NEEDS-ACTION',
    percentComplete: 0,
    completedDate: null,
    dtstamp: new Date(),
    recurrenceRule: originalTodo.recurrenceRule,
    alarms: updatedAlarms,
  }
}

/**
 * 기존 반복 todo를 다음 날짜로 업데이트
 * @param originalTodo 원본 반복 todo
 * @param completeCallback 완료 콜백
 */
const updateOriginalRecurringTodo = (
  originalTodo: TodoItem,
  completeCallback = (_success: boolean) => {},
) => {
  if (!originalTodo.recurrenceRule) {
    completeCallback(false)
    return
  }

  try {
    const rrule = originalTodo.recurrenceRule
    if (!rrule || !(rrule instanceof RRule)) {
      console.error('Invalid recurrence rule format:', rrule)
      completeCallback(false)
      return
    }

    // 다음 반복 날짜 계산
    const currentInstanceDate = originalTodo.dueDate || originalTodo.startDate || new Date()
    const hasAlarms = originalTodo.alarms && originalTodo.alarms.length > 0
    const nextDate = calculateNextRecurrenceDate(rrule, currentInstanceDate)

    if (!nextDate) {
      deleteTask(originalTodo, completeCallback)
      return
    }

    // 알림 존재 여부에 따른 datetype 결정
    const datetype = hasAlarms ? 'date-time' : originalTodo.datetype || 'date'

    // 날짜들 조정
    const { newStartDate, newDueDate } = adjustDatesForNextInstance(
      originalTodo,
      nextDate,
      datetype,
    )
    const normalizedNewDueDate = normalizeWithoutSeconds(newDueDate)

    // startDate와 알림 계산
    let computedStartDate = null
    let updatedAlarms = originalTodo.alarms || []

    if (hasAlarms && normalizedNewDueDate) {
      const firstAlarm = originalTodo.alarms[0]
      let originalReminderTime = null

      // reminder 시간 파싱
      if (firstAlarm.trigger && typeof firstAlarm.trigger === 'object' && firstAlarm.trigger.val) {
        // 절대 시간 알림 (UTC 형식)
        try {
          const triggerVal = firstAlarm.trigger.val
          let reminderDateTime

          if (typeof triggerVal === 'string' && triggerVal.match(/^\d{8}T\d{6}Z?$/)) {
            // iCalendar 형식: '20250808T020000Z' -> ISO 형식으로 변환
            const isoString = triggerVal.replace(
              /(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})(Z?)/,
              '$1-$2-$3T$4:$5:$6$7',
            )
            reminderDateTime = dayjs(isoString).toDate()
          } else {
            reminderDateTime = new Date(triggerVal)
          }

          if (!isNaN(reminderDateTime.getTime())) {
            originalReminderTime = normalizeWithoutSeconds(reminderDateTime)
          }
        } catch (error) {
          console.warn('Failed to parse reminder time:', firstAlarm.trigger.val, error)
        }
      } else if (typeof firstAlarm.trigger === 'string' && originalTodo.dueDate) {
        // 상대 시간 알림 - dueDate 기준으로 계산
        try {
          const dueDateTime = dayjs(originalTodo.dueDate).toDate()
          const seconds = secondsDuration(firstAlarm.trigger)
          const calculatedReminderTime = new Date(dueDateTime.getTime() + seconds * 1000)

          if (!isNaN(calculatedReminderTime.getTime())) {
            originalReminderTime = normalizeWithoutSeconds(calculatedReminderTime)
          }
        } catch (error) {
          console.warn('Failed to calculate relative reminder time:', firstAlarm.trigger, error)
        }
      }

      if (originalReminderTime && originalTodo.dueDate) {
        // 🔧 수정: reminder와 dueDate의 관계를 분석하여 적절한 방식 선택 (로컬 시간 기준으로 통일)
        const originalDueDate = new Date(originalTodo.dueDate)
        const originalReminderTimeLocal = new Date(originalReminderTime)

        // 계산을 위한 정규화된 시간 (로컬 시간 기준)
        const originalDueDateNormalized = moment(originalDueDate).startOf('day').toDate()
        const originalDueDateLocal = moment(originalDueDateNormalized)
        const originalReminderTimeLocalMoment = moment(originalReminderTimeLocal)

        // 원본에서 dueDate와 reminder가 같은 날인지 확인 (로컬 시간 기준)
        const dueDateOnly = moment(originalDueDate).format('YYYY-MM-DD')
        const reminderDateOnly = moment(originalReminderTime).format('YYYY-MM-DD')
        const isSameDay = dueDateOnly === reminderDateOnly

        console.log('🔧 Reminder relationship analysis:', {
          currentInstanceDate: currentInstanceDate.toString(),
          originalTodoDueDate: originalTodo.dueDate,
          originalDueDateLocal: originalDueDate.toString(),
          dueDateOnly,
          reminderDateOnly,
          isSameDay,
          originalDueDate: originalDueDateLocal.format('YYYY-MM-DD HH:mm:ss Z'),
          originalReminderTime: originalReminderTimeLocalMoment.format('YYYY-MM-DD HH:mm:ss Z'),
        })

        if (isSameDay) {
          // 같은 날 reminder: 시간만 복사 (로컬 시간 기준)
          const nextDateLocal = moment(nextDate).startOf('day')
          const newReminderTimeLocal = nextDateLocal
            .clone()
            .hour(originalReminderTimeLocalMoment.hour())
            .minute(originalReminderTimeLocalMoment.minute())
            .second(0)
            .millisecond(0)

          computedStartDate = newReminderTimeLocal.toDate()

          console.log('🔧 Same-day reminder (time-only approach):', {
            nextDate: nextDate.toISOString(),
            reminderTime: originalReminderTimeLocalMoment.format('HH:mm'),
            newReminderTimeLocal: newReminderTimeLocal.format('YYYY-MM-DD HH:mm:ss Z'),
            computedStartDate: computedStartDate.toISOString(),
          })
        } else {
          // Cross-date reminder: 전체 날짜+시간 차이 계산 (dueDate를 00:00:00으로 정규화하여 계산, 로컬 시간 기준)
          const totalDifferenceMs =
            originalReminderTimeLocalMoment.valueOf() - originalDueDateLocal.valueOf()

          console.log('🔧 Cross-date reminder (full date+time approach):', {
            totalDifferenceMs: totalDifferenceMs,
            totalDifferenceHours: totalDifferenceMs / (1000 * 60 * 60),
            nextDate: nextDate.toISOString(),
          })

          const nextDateLocal = moment(nextDate).startOf('day')
          const newReminderTimeLocal = nextDateLocal.clone().add(totalDifferenceMs, 'milliseconds')

          computedStartDate = newReminderTimeLocal.toDate()

          console.log('🔧 Cross-date reminder result:', {
            nextDateNormalized: nextDateLocal.format('YYYY-MM-DD HH:mm:ss Z'),
            newReminderTimeLocal: newReminderTimeLocal.format('YYYY-MM-DD HH:mm:ss Z'),
            computedStartDate: computedStartDate.toISOString(),
          })
        }

        // 알림 업데이트 - nextDate 기준으로 생성
        updatedAlarms = updateAlarmsForRecurrenceWithStartDate(
          originalTodo.alarms,
          computedStartDate,
        )
      } else {
        // 알림이 있지만 파싱 실패한 경우 nextDate를 00:00:00으로 정규화
        const nextDateNormalized = moment(nextDate).startOf('day').toDate()
        computedStartDate = nextDateNormalized
      }
    } else {
      computedStartDate = normalizeWithoutSeconds(newStartDate)
    }

    // 새 Todo 객체 생성 - nextDate를 dueDate로 사용 (00:00:00 정규화)
    const nextDateForNewDue = moment(nextDate).startOf('day').toDate() // 로컬 시간 기준으로 00:00:00 정규화
    const updatedTodo = createNextInstanceTodo(
      originalTodo,
      computedStartDate,
      nextDateForNewDue, // 정규화된 nextDate를 dueDate로 사용
      updatedAlarms,
      datetype,
    )

    // 기존 todo 업데이트 (동일한 UID 유지)
    updateTask(originalTodo.url, originalTodo.etag, updatedTodo, null, (success) => {
      if (success) {
        console.log(`Successfully updated recurring todo to next occurrence`)
      } else {
        console.error(`Failed to update recurring todo to next occurrence`)
      }
      completeCallback(success)
    })
  } catch (error) {
    console.error('Error updating original recurring todo:', error)
    completeCallback(false)
  }
}

/**
 * 다음 인스턴스를 위한 날짜 조정
 * @param originalDate 원본 날짜
 * @param nextRecurringDate 다음 반복 날짜
 * @param referenceDueDate 기준 due date
 * @param timezone 타임존 (선택사항)
 * @returns 조정된 날짜
 */
const adjustDateForNextInstance = (
  originalDate: string | Date | null,
  nextRecurringDate: Date,
  referenceDueDate: string | Date | null,
): string | null => {
  if (!originalDate) return null

  const original = moment(originalDate)
  const nextRecurring = moment(nextRecurringDate)
  const referenceDue = moment(referenceDueDate)

  if (!original.isValid() || !nextRecurring.isValid()) return null

  // due date를 기준으로 한 상대적 시간 차이 계산
  if (referenceDue.isValid()) {
    const timeDiff = original.diff(referenceDue)
    return nextRecurring.add(timeDiff, 'milliseconds').toISOString()
  }

  // 기준이 없으면 동일한 시간으로 설정
  return nextRecurring
    .hour(original.hour())
    .minute(original.minute())
    .second(original.second())
    .toISOString()
}

/**
 * UID로 Task 검색
 * @param uid Task UID
 * @param recurrenceId 반복 ID (선택사항)
 * @param completeCallback 완료 콜백
 */
export const searchTaskByUid = (
  uid: string,
  recurrenceId?: string,
  completeCallback = (_task: any) => {},
) => {
  const client = getClient()

  const xml =
    '<?xml version="1.0" encoding="utf-8" ?>' +
    '<C:calendar-query xmlns:C="urn:ietf:params:xml:ns:caldav">' +
    '<D:prop xmlns:D="DAV:">' +
    '<D:getetag/>' +
    '<C:calendar-data/>' +
    '</D:prop>' +
    '<C:filter>' +
    '<C:comp-filter name="VCALENDAR">' +
    '<C:comp-filter name="VTODO">' +
    '<C:prop-filter name="UID">' +
    '<C:text-match collation="i;unicode-casemap">' +
    uid +
    '</C:text-match>' +
    '</C:prop-filter>' +
    '</C:comp-filter>' +
    '</C:comp-filter>' +
    '</C:filter>' +
    '</C:calendar-query>'

  client
    .davRequest({
      url: client.account.homeUrl,
      init: {
        method: 'REPORT',
        headers: {
          Depth: 'infinity',
        },
        body: xml,
      },
      convertIncoming: false,
    })
    .then((response) => {
      console.log('search task by uid success %o', response)
      // 검색 결과 처리 로직 구현
      completeCallback(response)
    })
    .catch((error) => {
      console.log('search task by uid failed : %o', error)
      completeCallback(null)
    })
}

/**
 * Task 이동
 * @param targetFolder 대상 폴더
 * @param taskServerId Task 서버 ID
 * @param completeCallback 완료 콜백
 */
export const moveTask = (
  targetFolder: TodoFolder,
  taskServerId: string,
  completeCallback = (_success: boolean) => {},
) => {
  const client = getClient()

  const targetUrl = targetFolder.url + '/' + taskServerId.split('/').pop()
  client
    .davRequest({
      url: taskServerId,
      convertIncoming: false,
      init: {
        method: 'MOVE',
        body: '',
        headers: {
          Destination: targetUrl,
        },
      },
    })
    .then(async (response) => {
      console.log('move task success %o', response)

      // DB 업데이트: 서버 이동 성공 후 로컬 캐시 무효화 및 재동기화 필요
      // MOVE 작업은 복잡하므로 전체 동기화로 처리
      try {
        const { reloadTodos } = await import('./TodoSync')
        reloadTodos() // Move의 경우 전체 동기화가 필요
        console.log('Todo move completed, full sync triggered')
      } catch (syncError) {
        console.error('Failed to sync after todo move:', syncError)
      }

      enableTaskSyncByServerId(taskServerId)
      completeCallback(true)
    })
    .catch((error) => {
      console.log('move task failed : %o', error)
      completeCallback(false)
    })
}

/**
 * 서버 ID로 Task 동기화 활성화
 * @param taskServerId Task 서버 ID
 */
const enableTaskSyncByServerId = (taskServerId: string) => {
  // Todo 폴더 찾기 및 동기화 활성화 로직 구현
  console.log('Enable sync for task:', taskServerId)
}

/**
 * UID로 Task 찾기 (내부 함수)
 * @param uid Task UID
 * @param recurrenceId 반복 ID (선택사항)
 * @param callback 콜백
 */
const findTaskByUid = (
  uid: string,
  recurrenceId: string | undefined,
  callback: (task: any) => void,
) => {
  // Task 인스턴스 찾기 로직 구현
  // 임시로 null 반환
  callback(null)
}
